import { isEmpty } from 'lodash';
import type {
    ApiLiveDriver,
    ApiLiveStop,
    AssignedDepot,
    SocketDriver,
    SocketScheduleItem
} from '~/api/types';
import { DepotStopNumbers } from '~/api/types';
import { LiveDriver } from '~/data-classes';
import { isAssignmentLate } from '~/utils/assignment-utils';

/**
 * Return type internal to this file
 */
type ProcessedSchedule = {
    schedule: ApiLiveStop[];
    numLateExceptions: number;
};

interface MakeLiveDriverScheduleJSONParams {
    webSchedule: SocketScheduleItem[];
    startDepot: AssignedDepot | null;
    endDepot: AssignedDepot | null;
}

const makeLiveDriverScheduleJSON = ({
    webSchedule,
    startDepot,
    endDepot
}: MakeLiveDriverScheduleJSONParams): ProcessedSchedule => {
    let resequenceIndex = 1;
    let completedTasksCount = 0;
    return webSchedule.reduce<ProcessedSchedule>(
        (acc, webStop, stopIndex) => {
            const {
                taskLabels: labels,
                inventoryItems: inventory,
                props: {
                    serviceTime,
                    priority,
                    window: timeWindow,
                    rawTimeWindow,
                    size,
                    weight,
                    height
                },
                stopnumber,
                depotId,
                isDepot,
                isCompleted
            } = webStop;

            const updatedStopNumber = isDepot
                ? stopIndex
                : (stopnumber as number);
            const driverStopNumber = isDepot ? stopIndex : resequenceIndex;

            const isStartDepot = Boolean(
                isDepot &&
                    stopnumber === DepotStopNumbers.START_DEPOT &&
                    depotId &&
                    depotId === startDepot?.id
            );

            const isEndDepot = Boolean(
                isDepot &&
                    stopnumber === DepotStopNumbers.END_DEPOT &&
                    depotId &&
                    depotId === endDepot?.id
            );

            if (!isDepot) {
                resequenceIndex++;
            }
            if (isCompleted && !isDepot) {
                completedTasksCount++;
            }

            const pendingStopsPosition = driverStopNumber - completedTasksCount;
            const liveStop: ApiLiveStop = {
                ...webStop,
                driverStopNumber,
                stopNumber: updatedStopNumber,
                isStartDepot,
                isEndDepot,
                labels,
                serviceTime,
                priority,
                timeWindow,
                rawTimeWindow,
                size,
                weight,
                height,
                stopPosition: pendingStopsPosition,
                hasCompletedTasks: Boolean(completedTasksCount),
                inventory
            };

            const lateExceptions = isAssignmentLate(liveStop.delay) ? 1 : 0;

            return {
                schedule: [...acc.schedule, liveStop],
                numLateExceptions: acc.numLateExceptions + lateExceptions
            };
        },
        {
            schedule: [],
            numLateExceptions: 0
        }
    );
};

export const makeLiveDriver = (webDriver: SocketDriver) => {
    const {
        id: driverId,
        eid,
        client: clientId,
        lastLocationUpdate,
        location: cepLocation,
        endingShift: isEndingShift,
        profile: {
            name: profileName,
            initials: profileInitials,
            firstname: profileFirstName,
            lastname: profileLastName
        },
        vehicle: {
            eid: vehicleEid,
            id: vehicleId,
            type: vehicleType,
            weightCapacity: vehicleWeightCapacity,
            volumeCapacity: vehicleVolumeCapacity
        },
        stats: {
            totalUnits: volumeCapacityUsed,
            totalWeight: weightCapacityUsed,
            activeAssignmentIndex: currentStop,
            totalSch: numStops,
            timeRemaining: { ms: timeRemaining },
            driverHasActiveLocation: active,
            numInventoryItems,
            numConfirmedInventoryItems,
            numInventoryExceptions,
            atRiskWatch: numAtRiskExceptions,
            lateWatch: statsNumLateExceptions,
            isDriverComplete
        },
        startDepot,
        endDepot,
        schedule: webDriverSchedule
    } = webDriver;

    const startDepotData = !isEmpty(startDepot) ? startDepot : null;
    const endDepotData = !isEmpty(endDepot) ? endDepot : null;

    const { schedule, numLateExceptions: scheduleNumLateExceptions } =
        makeLiveDriverScheduleJSON({
            webSchedule: webDriverSchedule,
            startDepot: startDepotData,
            endDepot: endDepotData
        });

    // late exceptions are no longer compiled upstream once all assignments are completed.
    // derive from schedule when driver is complete
    const numLateExceptions = isDriverComplete
        ? scheduleNumLateExceptions
        : statsNumLateExceptions;

    const liveDriver: ApiLiveDriver = {
        id: driverId,
        clientId,
        routeName: vehicleEid || eid,
        lastLocationUpdate,
        cepLocation,
        latestLocationUpdate: null,
        routeCenter: null,
        depot: {
            startDepot: startDepotData,
            endDepot: endDepotData
        },
        profile: {
            name: profileName,
            initials: profileInitials,
            firstname: profileFirstName,
            lastname: profileLastName
        },
        vehicle: {
            id: vehicleId,
            type: vehicleType,
            maxWeight: vehicleWeightCapacity,
            maxVolume: vehicleVolumeCapacity,
            stats: {
                volumeCapacityUsed,
                weightCapacityUsed
            }
        },
        stats: {
            currentStop,
            numStops,
            timeRemaining,
            active,
            numInventoryItems,
            numConfirmedInventoryItems,
            numInventoryExceptions,
            numAtRiskExceptions,
            numLateExceptions,
            isDriverComplete,
            isEndingShift
        },
        schedule
    };

    return new LiveDriver(liveDriver);
};
